
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  *
  * The source file is located at:
  *   /codebuild/output/src2723206075/src/vl-app/pages/clinician/profile.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Input, Select, Button, Card } from 'antd';
import styled from '@emotion/styled';
import Grid from '@components/UI/Grid';
import { errorModal, successModal } from 'vl-common/src/components/Modals';
import { getMetaCliniciansQuery } from 'vl-common/src/schemas/getMetaClinicians';
import useClinician from '@hooks/useClinician';
import SessionLoader from '@components/Clinicians/SessionLoader';
import useApi from 'vl-common/src/hooks/useApi';
import { useTranslation } from 'react-i18next';
import Header from '@components/Layouts/Header/Header';

const ContentWrapper = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  padding: 0 1rem 2rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledCard = styled(Card)`
  margin-bottom: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .ant-card-head {
    min-height: 42px;
    border-bottom: none;
  }

  .ant-card-body {
    padding: 0 24px 12px;
  }
`;

const DisabledInputBase = (props: React.ComponentProps<typeof Input>) => <Input disabled {...props} />;

const DisabledInput = styled(DisabledInputBase)`
  &,
  .ant-picker-input > input[disabled],
  .ant-select-selector,
  .ant-input {
    cursor: default !important;
    color: #000 !important;
  }
`;

const Hint = styled.span<{ warning?: boolean }>`
  font-size: 0.8rem;
  ${({ warning }) => {
    const color = warning ? 'var(--productPrimary)' : 'var(--productGray)';

    return `
      color: ${color}
    `;
  }}
`;

const BioHintRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const BioLimitCounterWrapper = styled.div<{ full?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ full }) => (full ? 'var(--productRed)' : 'var(--productGray)')};
  font-size: 0.8rem;
`;

const CompactFormItem = styled(Form.Item)`
  margin-bottom: 4px;
`;

type ProfileHeaderActionsProps = {
  editing: boolean;
  saving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
};

const ProfileHeaderActions = ({ editing, saving, onEdit, onCancel, onSave }: ProfileHeaderActionsProps) => {
  const { t } = useTranslation('common');
  if (!editing) {
    return (
      <Button type="primary" onClick={onEdit} data-testid="edit-profile-button">
        {t('Edit')}
      </Button>
    );
  }

  return (
    <>
      <Button onClick={onCancel} disabled={saving} data-testid="cancel-edit-profile-button">
        {t('Cancel')}
      </Button>
      <Button type="primary" loading={saving} onClick={onSave} data-testid="save-profile-button">
        {t('Save')}
      </Button>
    </>
  );
};

const BioLimitCounter = ({ contentLength }: { contentLength: number }) => {
  const percentage = Math.min(Math.ceil((contentLength / BIO_MAX_LENGTH) * 100), 100);
  const isFull = percentage >= 100;

  return (
    <BioLimitCounterWrapper full={isFull}>
      <svg height="16" width="16" viewBox="0 0 20 20">
        <circle r="10" cx="10" cy="10" fill="#e9ecef" />
        <circle
          r="5"
          cx="10"
          cy="10"
          fill="transparent"
          stroke="currentColor"
          strokeWidth="10"
          strokeDasharray={`${(percentage / 100) * 31.4} 31.4`}
          transform="rotate(-90) translate(-20)"
        />
        <circle r="6" cx="10" cy="10" fill="white" />
      </svg>
      <span>{percentage}%</span>
    </BioLimitCounterWrapper>
  );
};

const BIO_MAX_LENGTH = 2048;

const ClinicianProfile = () => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const [form] = Form.useForm();

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedClinicalInterests, setSelectedInterests] = useState<string[]>([]);

  const { data: clinicianMeta, isLoading: isLoadingClinicianMeta } = useQuery(getMetaCliniciansQuery());

  const { clinician, isLoading: isLoadingClinicianProfile } = useClinician();

  const setInitialProfileFormData = () => {
    if (!clinician) return;

    const initialInterests = clinician?.interests?.map((i) => i.interest_code) ?? [];
    form.setFieldsValue({
      title: clinician.title,
      first_name: clinician.first_name,
      last_name: clinician.last_name,
      email: clinician.email,
      bio: clinician.bio,
      interests: initialInterests ?? []
    });
    setSelectedInterests(initialInterests);
  };

  useEffect(() => {
    if (!clinician) return;

    setInitialProfileFormData();
  }, [clinician]);

  const queryClient = useQueryClient();

  const { mutateAsync: updateProfile, isPending: saving } = useMutation({
    mutationFn: async () => {
      const { bio, interests } = form.getFieldsValue(['bio', 'interests']);
      await Api.clinicians.updateClinicianBio({ bio, interests });
    },
    onSuccess: () => {
      setIsEditMode(false);
      queryClient.invalidateQueries({ queryKey: ['getClinician'] });
      successModal({
        title: t('Profile updated'),
        content: t('Your profile has been updated successfully.')
      });
    },
    onError: (error: any) => {
      errorModal({
        title: t('Failed to update profile'),
        content:
          error?.response?.data?.message ?? t('Something went wrong while saving your profile. Please try again.')
      });
    }
  });

  const bioField = Form.useWatch('bio', { form });

  if (isLoadingClinicianMeta || isLoadingClinicianProfile) {
    return <SessionLoader message={t('Loading profile data...')} />;
  }

  const maxInterestCount = clinicianMeta?.max_interests[0].max_interests ?? 0;
  const interestsOptions =
    clinicianMeta?.special_interests?.map((interest) => ({
      value: interest.interest_code,
      label: interest.interest_desc
    })) ?? [];

  const canSelectInterests = !!interestsOptions.length;

  const handleEdit = () => setIsEditMode(true);

  const handleCancel = () => {
    setInitialProfileFormData();
    setIsEditMode(false);
  };

  return (
    <>
      <Header pageType="clinician" pageTitle={t('Profile')} />

      <section className="section-layout">
        <main className="container" id="page-content">
          <ContentWrapper>
            <PageHeader>
              <PageTitle>{t('My Profile')}</PageTitle>
              <HeaderActions>
                <ProfileHeaderActions
                  editing={isEditMode}
                  saving={saving}
                  onEdit={handleEdit}
                  onCancel={handleCancel}
                  onSave={form.submit}
                />
              </HeaderActions>
            </PageHeader>

            <Form form={form} layout="vertical" onFinish={updateProfile}>
              <StyledCard title={t('Personal Details')} data-testid="personal-details-card">
                <Grid columns={3}>
                  <Form.Item label={t('Title')} name="title">
                    <DisabledInput data-testid="title" />
                  </Form.Item>
                  <Form.Item label={t('First Name')} name="first_name">
                    <DisabledInput data-testid="first_name" />
                  </Form.Item>
                  <Form.Item label={t('Last Name')} name="last_name">
                    <DisabledInput data-testid="last_name" />
                  </Form.Item>
                </Grid>
                <Grid columns={1}>
                  <Form.Item label={t('Email Address')} name="email">
                    <DisabledInput data-testid="email" />
                  </Form.Item>
                </Grid>
              </StyledCard>

              <StyledCard title={t('Clinical Bio')} data-testid="clinical-bio-card">
                <CompactFormItem label={t('Biography')} name="bio">
                  <Input.TextArea
                    data-testid="bio"
                    rows={4}
                    maxLength={BIO_MAX_LENGTH}
                    placeholder={t('Enter your professional biography')}
                    disabled={!isEditMode || saving}
                  />
                </CompactFormItem>
                <BioHintRow>
                  <Hint data-testid="clinical-bio-hint">
                    {t('This bio will be displayed to patients on your profile')}
                  </Hint>
                  {isEditMode && (
                    <BioLimitCounter data-testid="clinical-bio-limit-hint" contentLength={bioField?.length ?? 0} />
                  )}
                </BioHintRow>
              </StyledCard>

              {canSelectInterests && (
                <StyledCard title={t('Clinical Interests')} data-testid="clinical-interests-card">
                  <CompactFormItem
                    label={t('Select up to {{maxInterestCount}} clinical interests', { maxInterestCount })}
                    name="interests"
                  >
                    <Select
                      data-testid="clinical-interests-select"
                      mode="multiple"
                      virtual={false}
                      disabled={!isEditMode || saving}
                      options={interestsOptions.map((opt) => ({
                        ...opt,
                        disabled:
                          selectedClinicalInterests.length >= maxInterestCount &&
                          !selectedClinicalInterests.includes(opt.value)
                      }))}
                      placeholder={t('Select clinical interests')}
                      onChange={(values: string[]) => setSelectedInterests(values)}
                    />
                  </CompactFormItem>
                  <Hint
                    data-testid="clinical-interests-hint"
                    warning={selectedClinicalInterests.length >= maxInterestCount}
                  >
                    {t('Selected {{count}}/{{max}}', {
                      count: selectedClinicalInterests.length,
                      max: maxInterestCount
                    })}
                  </Hint>
                </StyledCard>
              )}
            </Form>
          </ContentWrapper>
        </main>
      </section>
    </>
  );
};


export const Route = createFileRoute('/clinician/profile')({ component: ClinicianProfile });
